import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedUserId: string | null;
  originalAdminUserId: string | null;
  impersonatedUserData: any | null;
  startedAt: string | null;
  impersonationLogId: string | null;
}

interface ImpersonationContextType extends ImpersonationState {
  startImpersonation: (userId: string, adminId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const STORAGE_KEY = 'comparare_impersonation_state';

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ImpersonationState>(() => {
    // Load from localStorage on init
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error loading impersonation state:', e);
    }
    
    return {
      isImpersonating: false,
      impersonatedUserId: null,
      originalAdminUserId: null,
      impersonatedUserData: null,
      startedAt: null,
      impersonationLogId: null,
    };
  });

  // Persist to localStorage whenever state changes
  useEffect(() => {
    if (state.isImpersonating) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [state]);

  const startImpersonation = async (userId: string, adminId: string) => {
    try {
      // Fetch the impersonated user's profile data
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (userError) throw userError;

      // Create audit log entry
      const { data: logEntry, error: logError } = await supabase
        .from('admin_impersonation_log')
        .insert({
          admin_user_id: adminId,
          impersonated_user_id: userId,
        })
        .select()
        .single();

      if (logError) throw logError;

      const newState: ImpersonationState = {
        isImpersonating: true,
        impersonatedUserId: userId,
        originalAdminUserId: adminId,
        impersonatedUserData: userData,
        startedAt: new Date().toISOString(),
        impersonationLogId: logEntry.id,
      };

      setState(newState);
    } catch (error) {
      console.error('Error starting impersonation:', error);
      throw error;
    }
  };

  const stopImpersonation = async () => {
    if (!state.isImpersonating || !state.impersonationLogId || !state.startedAt) {
      setState({
        isImpersonating: false,
        impersonatedUserId: null,
        originalAdminUserId: null,
        impersonatedUserData: null,
        startedAt: null,
        impersonationLogId: null,
      });
      return;
    }

    try {
      // Calculate duration
      const startTime = new Date(state.startedAt);
      const endTime = new Date();
      const durationSeconds = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);

      // Update audit log
      await supabase
        .from('admin_impersonation_log')
        .update({
          ended_at: endTime.toISOString(),
          session_duration_seconds: durationSeconds,
        })
        .eq('id', state.impersonationLogId);
    } catch (error) {
      console.error('Error updating impersonation log:', error);
    }

    // Clear state regardless of log update success
    setState({
      isImpersonating: false,
      impersonatedUserId: null,
      originalAdminUserId: null,
      impersonatedUserData: null,
      startedAt: null,
      impersonationLogId: null,
    });
  };

  return (
    <ImpersonationContext.Provider
      value={{
        ...state,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
}
